import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/* External */
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import PrintIcon from '@material-ui/icons/Print';
import AddIcon from '@material-ui/icons/LibraryAdd';
import PostAdd from '@material-ui/icons/PostAdd';
import { CircularProgress } from '@material-ui/core';

/* Internal */
import StyledTableCell from 'commons/components/StyledTableCell';
import DatePicker from 'commons/components/DatePicker';
import DeleteModal from 'commons/components/DeleteModal';
import { truthty, falsy, roundNumber, getUnitDetails } from 'utils/functions';
import ConfirmDialog from 'commons/components/ConfirmDialog';
import IconButtonWithLoader from 'commons/containers/IconButtonWithLoader';
import { sendAlert } from 'utils/alertUtils';
import DialButton from './DialButton';
import Entries from './Entries';
import Departures from './Departures';
import WorkShiftStaff from './WorkShiftStaff';
import SupplyProductLines from './SupplyProductLines';
import StaffForm from './StaffForm';
import LotForm from './LotForm';
import SupplyProductForm from './SupplyProductForm';
import EntryForm from './EntryForm';
import ProcessEquipmentForm from './ProcessEquipmentForm';
import Headers from './Headers';

const withRenderKey = (lot, renderKey) => ({
  ...lot,
  renderKey,
});

const getManualLots = (lots, processId, relationField, prefix) =>
  Object.values(lots)
    .filter((lot) => lot[relationField] === processId)
    .map((lot) => withRenderKey(lot, `${prefix}-${lot.id}`));

const getAutomationLots = (lots, processTransitions, processId, relationField, prefix) => {
  const lotsById = Object.values(lots).reduce(
    (accumulator, lot) => ({
      ...accumulator,
      [lot.id]: lot,
    }),
    {},
  );

  return Object.values(processTransitions)
    .filter((transition) => transition[relationField] === processId)
    .map((transition) => {
      const lot = lotsById[transition.lotId];
      if (!lot) return null;

      return withRenderKey(lot, `${prefix}-${transition.id}-${transition.lotId}`);
    })
    .filter(Boolean);
};

function ProcessComponent(props) {
  const { t } = useTranslation();
  const {
    classes,
    lotId,
    process,
    multipleLotsProcess,
    uniqueLot,
    objects,
    toggleDelete,
    processStaffLines,
    printFnc,
    printFncPaks,
    formControls,
    invalidControls,
    formIsOpen,
    changeControls,
    changeControlsNeedsEvent,
    submitStaff,
    toggleForm,
    employeeOptions,
    submitLot,
    changeWeight,
    totalWeight,
    submitEntry,
    qrReader,
    qrCode,
    onChangeFnc,
    submitSupplyProduct,
    role,
    submitDelete,
    speedDialToggle,
    dialButtonOpen,
    closeProcess,
    toggleClose,
    printAllFnc,
    processResources,
    equipmentOptions,
    submitProcessEquipments,
    isAdmin,
    infoLot,
    usedLot,
    actions,
    stopLoading,
    infoEntryLots,
    objects: { lots, products, varieties, fruits, packagingTypes, equipments },
    onBackAction,
    hasAutomation,
    delete: { isOpen: isDeleteOpen },
  } = props;

  const processTransitions = objects.processTransitions || {};
  const { unitName } = getUnitDetails(objects.accounts);

  const infoMsg = {
    open: t('process.openProcessMessage'),
    close: t('process.closeProcessMessage'),
  };

  const manualEntryLots = getManualLots(
    lots,
    process.id,
    'destinyProcessId',
    `manual-entry-${process.id}`,
  );
  const manualDepartureLots = getManualLots(
    lots,
    process.id,
    'originProcessId',
    `manual-departure-${process.id}`,
  );

  const automationEntryLots = hasAutomation
    ? getAutomationLots(
        lots,
        processTransitions,
        process.id,
        'destinationProcessId',
        `automation-entry-${process.id}`,
      )
    : [];
  const automationDepartureLots = hasAutomation
    ? getAutomationLots(
        lots,
        processTransitions,
        process.id,
        'originProcessId',
        `automation-departure-${process.id}`,
      )
    : [];

  const entryLots = hasAutomation
    ? [
        ...automationEntryLots,
        ...manualEntryLots.filter(
          (lot) => !automationEntryLots.some((automationLot) => automationLot.id === lot.id),
        ),
      ]
    : manualEntryLots;

  const departureLots = hasAutomation
    ? [
        ...automationDepartureLots,
        ...manualDepartureLots.filter(
          (lot) => !automationDepartureLots.some((automationLot) => automationLot.id === lot.id),
        ),
      ]
    : manualDepartureLots;

  const entryWeight = entryLots.reduce((acum, l) => acum + l.quantity, 0);
  const departureWeight = departureLots.reduce((acum, l) => acum + l.quantity, 0);

  const hasBalanceError = departureWeight > entryWeight;

  const warningMsg = () => {
    const msg = [];
    if (entryWeight <= 0) msg.push(t('process.noPaksInput'));
    if (departureWeight <= 0) msg.push(t('process.noPaksOutput'));
    if (entryWeight - departureWeight < 0)
      msg.push(t('process.wasteMessage', { amount: entryWeight - departureWeight }));
    if (msg.length > 0) msg.unshift(t('process.processNo', { id: process.id }));

    return msg.length > 0 ? msg.join(', ') : null;
  };

  const getLotRelation = (lot, fnName, fallbackMap, fallbackIdField) =>
    (typeof lot?.[fnName] === 'function' && lot[fnName]()) ||
    fallbackMap?.[lot?.[fallbackIdField]] ||
    null;

  // useEffect para envio de alertas por diferencia de pesos al cargar o actulizar.
  useEffect(() => {
    // setEntryWeightValidate(entryWeight);
    // setDepartureWeightValidate(departureWeight);

    if (hasBalanceError && lots && Object.keys(lots).length > 0) {
      sendAlert({
        type: 'balance_error',
        processId: process.id,
        title: t('process.balanceErrorTitle'),
        description: t('process.balanceErrorMessage', { id: process.id }),
        inputWeight: entryWeight,
        outputWeight: departureWeight,
      });
    }
  }, [entryWeight, departureWeight, hasBalanceError]);

  const getProductsHeaders = (departureHeaders = false) => {
    // let headers = ['N° Pak', 'Especie', 'Variedad', 'Producto', 'N° Envases', 'Cantidad', 'Costo Pak', falsy(departureHeaders) && falsy(process.closedAt) && ' '];
    let headers = [
      'N° Pak',
      t('Species'),
      t('Variety'),
      t('Product'),
      t('receipts.form.containerQuantity'),
      `${t('Quantity')} ${unitName}`,
      falsy(departureHeaders) && falsy(process.closedAt) && ' ',
    ];
    if (formControls.headers.isGroupedInfo) {
      headers = [
        t('Quantity'),
        t('Species'),
        t('Variety'),
        t('Product'),
        `${unitName} ${t('Total').toLowerCase()}`,
      ];
    }
    return headers.map((header) => <StyledTableCell align="right">{header}</StyledTableCell>);
  };

  const processEquipments = Object.values(objects.processEquipments).filter(
    (pe) => pe.processId === process.id,
  );
  const isSmallScreen = window.innerWidth < 600;

  const getProductsRows = (filteredLots = [], isDepartures = false) => {
    let rows = [];

    if (formControls.headers.isGroupedInfo) {
      rows = filteredLots
        .reduce((acum, lot) => {
          const newAcum = acum;
          const { quantity, fruitId, varietyId, productId, price } = lot;
          const index = acum.findIndex(
            (elem) =>
              fruitId === elem.fruitId &&
              elem.varietyId === varietyId &&
              productId === elem.productId,
          );

          if (index === -1) {
            newAcum.push({
              quantity: 1,
              totalWeight: quantity,
              fruitId,
              varietyId,
              productId,
              totalPrice: price,
            });
          } else {
            const rowFunded = acum[index];
            newAcum[index] = {
              ...rowFunded,
              totalWeight: rowFunded.totalWeight + quantity,
              quantity: rowFunded.quantity + 1,
              totalPrice: rowFunded.price,
            };
          }

          return acum;
        }, [])
        .map((row) => {
          const product = products[row.productId];
          const variety = varieties[row.varietyId];
          const fruit = fruits[row.fruitId];

          return (
            <TableRow
              key={`group-${row.fruitId.toString()}-${row.varietyId.toString()}-${product.id}`}>
              <StyledTableCell align="right" component="th" scope="lot">
                {row.quantity}
              </StyledTableCell>
              <StyledTableCell align="right">{fruit?.name}</StyledTableCell>
              <StyledTableCell align="right">{variety?.name}</StyledTableCell>
              <StyledTableCell align="right">{product.name}</StyledTableCell>
              <StyledTableCell align="right">{roundNumber(row.totalWeight)}</StyledTableCell>
            </TableRow>
          );
        });
    } else {
      rows = filteredLots.map((lot) => (
        <TableRow key={lot.renderKey || `departure-lot-${lot.id}`}>
          <StyledTableCell align="center" component="th" scope="lot">
            {lot.id}
          </StyledTableCell>
          <StyledTableCell>{getLotRelation(lot, 'fruit', fruits, 'fruitId')?.name}</StyledTableCell>
          <StyledTableCell align="right">
            {getLotRelation(lot, 'variety', varieties, 'varietyId')?.name}
          </StyledTableCell>
          <StyledTableCell align="right">
            {getLotRelation(lot, 'product', products, 'productId')?.name}
          </StyledTableCell>
          <StyledTableCell align="right">
            {`${lot.packagingQuantity} [${
              getLotRelation(lot, 'packagingType', packagingTypes, 'packagingTypeId')?.name
            }]`}
          </StyledTableCell>
          <StyledTableCell align="right">{roundNumber(lot.quantity)}</StyledTableCell>
          <StyledTableCell align="right">
            {!isSmallScreen && (
              <IconButtonWithLoader
                // @ts-ignore
                buttonId="print"
                onClick={printFnc(lot.id)}
                size="small"
                className="hiddem">
                <PrintIcon fontSize="small" />
              </IconButtonWithLoader>
            )}
            {falsy(process.closedAt) && (
              <IconButton
                onClick={toggleDelete}
                data-id={lot.id}
                data-module={falsy(isDepartures) ? 'deleteEntry' : 'deleteLot'}
                size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            {!isSmallScreen && (
              <>
                <Tooltip title={t('process.printByContainer')}>
                  <IconButton
                    onClick={() => printFncPaks(lot.id, lot.packagingQuantity)}
                    data-id="print-paks"
                    size="small">
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {isDepartures && (
                  <Tooltip title={t('process.createMultipleCopies')}>
                    <IconButton
                      onClick={(event) => toggleForm(event)}
                      data-id={lot.id}
                      data-module="departure"
                      size="small">
                      <PostAdd fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </StyledTableCell>
        </TableRow>
      ));
    }

    return rows;
  };

  return (
    <>
      <Grid container spacing={3}>
        {lotId ? (
          <Headers
            classes={classes}
            process={process}
            multipleLotsProcess={multipleLotsProcess}
            uniqueLot={uniqueLot}
            toggleClose={toggleClose}
            isAdmin={isAdmin}
            changeControls={changeControls}
            controls={formControls.headers}
            products={products}
            processEquipments={processEquipments}
            equipments={equipments}
            toggleDelete={toggleDelete}
            onBackAction={onBackAction}
            hasBalanceError={hasBalanceError}
            role={role}
          />
        ) : (
          <Grid item container justifyContent="center" alignContent="center">
            <CircularProgress />
          </Grid>
        )}
        {lotId && (
          <>
            <Grid item xs={12} md={7}>
              <Entries
                isGroupedInfo={formControls.headers.isGroupedInfo}
                classes={classes}
                process={process}
                lots={objects.lots}
                objects={objects}
                toggleDelete={toggleDelete}
                entryLots={entryLots}
                printAllFnc={printAllFnc}
                getProductsRows={getProductsRows}
                getProductsHeaders={getProductsHeaders}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <WorkShiftStaff
                classes={classes}
                processStaffLines={processStaffLines}
                process={process}
                toggleDelete={toggleDelete}
                processResources={processResources}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Departures
                isGroupedInfo={formControls.isGroupedInfo}
                classes={classes}
                process={process}
                lots={objects.lots}
                objects={objects}
                toggleDelete={toggleDelete}
                printFnc={printFnc}
                // printFncPaks={printFncPaks}
                // printPakLabels={printPakLabels}
                printAllFnc={printAllFnc}
                getProductsRows={getProductsRows}
                getProductsHeaders={getProductsHeaders}
                departureLots={departureLots}
                isSmallScreen={isSmallScreen}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <SupplyProductLines
                classes={classes}
                supplyLines={objects.supplyLines}
                process={process}
                toggleDelete={toggleDelete}
              />
            </Grid>
          </>
        )}
      </Grid>
      <StaffForm
        employees={objects.employees}
        controls={formControls.staff}
        invalidControls={invalidControls.staff}
        isOpen={formIsOpen.staff}
        changeControls={changeControls}
        submit={submitStaff}
        toggleForm={toggleForm}
        employeeOptions={employeeOptions}
        processResources={processResources}
      />
      <ProcessEquipmentForm
        employees={objects.employees}
        controls={formControls.processEquipments}
        invalidControls={invalidControls.processEquipments}
        isOpen={formIsOpen.processEquipments}
        changeControls={changeControls}
        submit={submitProcessEquipments}
        toggleForm={toggleForm}
        equipmentOptions={equipmentOptions}
        processResources={processResources}
      />
      <LotForm
        changeControlsNeedsEvent={changeControlsNeedsEvent}
        controls={formControls.departure}
        invalidControls={invalidControls.departure}
        isOpen={formIsOpen.departure}
        warehouses={objects.warehouses}
        products={objects.products}
        fruits={objects.fruits}
        varieties={objects.varieties}
        objects={objects}
        packagingTypes={objects.packagingTypes}
        changeControls={changeControls}
        submit={submitLot}
        toggleForm={toggleForm}
        changeWeight={changeWeight}
        totalWeight={totalWeight}
        entryLots={entryLots}
        process={process}
        module="departure"
        actions={actions}
      />
      {formIsOpen.entry && (
        <EntryForm
          process={process}
          controls={formControls.entry}
          lots={objects.lots}
          invalidControls={invalidControls.entry}
          isOpen={formIsOpen.entry}
          changeControls={changeControls}
          submit={submitEntry}
          toggleForm={toggleForm}
          qrReader={qrReader}
          onChangeFnc={onChangeFnc}
          qrCode={qrCode}
          uniqueLot={uniqueLot}
          getProductsRows={getProductsRows}
          getProductsHeaders={getProductsHeaders}
          isGroupedInfo={formControls.headers.isGroupedInfo}
          classes={classes}
          toggleDelete={toggleDelete}
          entryLots={entryLots}
          printAllFnc={printAllFnc}
          formControls={formControls}
          objects={objects}
          infoEntryLots={infoEntryLots}
          lot={infoLot}
          usedLot={usedLot}
          actions={actions}
          stopLoading={stopLoading}
        />
      )}
      <SupplyProductForm
        controls={formControls.supplyProducts}
        invalidControls={invalidControls.supplyProducts}
        isOpen={formIsOpen.supplyProducts}
        changeControls={changeControls}
        submit={submitSupplyProduct}
        toggleForm={toggleForm}
        supplyProducts={objects.supplyProducts}
        warehouses={objects.warehouses}
        purchases={objects.purchases}
        role={role}
      />
      <DeleteModal
        process={process}
        isOpen={isDeleteOpen}
        toggleForm={toggleDelete}
        submit={submitDelete}
        buttonId="delete-process-item"
      />
      {lotId && falsy(process.closedAt) && (
        <DialButton
          speedDialToggle={speedDialToggle}
          open={dialButtonOpen}
          classes={classes}
          toggleForm={toggleForm}
          disabledDepartures={entryLots.length === 0}
        />
      )}
      <ConfirmDialog
        title={
          truthty(process.closedAt)
            ? t('process.openProcessTitle', { id: process.id })
            : t('process.finishProcessTitle', { id: process.id })
        }
        open={formControls.closeModal.isOpen}
        maxWidth="sm"
        message={
          truthty(process.closedAt)
            ? t('process.openProcessConfirm', { id: process.id })
            : t('process.closeProcessSelectDate')
        }
        accept={() => {
          const date = truthty(process.closedAt) ? null : formControls.closeModal.closedAt;
          closeProcess(process.id, date);
        }}
        cancel={toggleClose}
        alert={warningMsg()}
        info={truthty(process.closedAt) ? infoMsg.open : infoMsg.close}>
        {falsy(process.closedAt) && (
          <Grid container justifyContent="center">
            <Grid item xs={6}>
              <DatePicker
                inputId="closedAt"
                label={t('process.closingDate')}
                fullWidth
                value={formControls.closeModal.closedAt}
                onChange={changeControls}
                inputProps={{ 'data-module': 'closeModal' }}
                type="datetime-local"
              />
            </Grid>
          </Grid>
        )}
      </ConfirmDialog>
    </>
  );
}

export default ProcessComponent;
